// Régua de SINAL do lançamento manual (2026-09-17).
//
// 🚨 Por que isto existe: a tela decidia "é receita ou despesa?" olhando o
// SINAL do valor (`valor >= 0`). Funcionou enquanto toda receita era positiva
// — até aparecer o ESTORNO DE COMPETÊNCIA: receita NEGATIVA, que tira de um
// ano o que foi reconhecido no outro. O caso real: o adiantamento Corsan do
// contrato HY25014, faturado inteiro em 2025 e executado em 2026. Os dois
// lançamentos que jogaram R$ 496.100 em 2026 existiam desde abril/julho de
// 2026; a contrapartida negativa em 2025 só entrou em 17/09/2026, e até então
// o mesmo serviço era reconhecido nos DOIS anos.
//
// Com a regra do sinal, esse lançamento abria no modal de edição como DESPESA,
// oferecia o catálogo de despesas, e bastava alguém escolher uma categoria de
// lá para o estorno ser gravado no grupo errado.
//
// Quem manda agora é o GRUPO da DRE. O sinal só decide quando não há grupo
// gravado (lançamento legado).

export const GRUPOS_RECEITA: readonly string[] = [
  'RECEITA_BRUTA',
  'OUTRAS_RECEITAS_OPERACIONAIS',
  'RECEITAS_FINANCEIRAS',
];

export type TipoLancamento = 'receita' | 'despesa';

export type LancamentoSinal = {
  grupo_dre?: string | null;
  valor?: number | null;
};

/** É lançamento de RECEITA? O grupo manda; sem grupo, cai no sinal (legado). */
export function ehReceita(l: LancamentoSinal): boolean {
  const grupo = (l.grupo_dre || '').trim();
  if (grupo) return GRUPOS_RECEITA.includes(grupo);
  return (l.valor || 0) >= 0;
}

/** O tipo, do jeito que a tela chama. */
export function tipoDoLancamento(l: LancamentoSinal): TipoLancamento {
  return ehReceita(l) ? 'receita' : 'despesa';
}

/**
 * É um estorno? Receita com valor negativo, ou despesa com valor positivo —
 * o sinal ao contrário do que aquele tipo normalmente tem. Valor zero não é
 * estorno de nada.
 */
export function ehEstorno(l: LancamentoSinal): boolean {
  const v = l.valor || 0;
  if (v === 0) return false;
  return ehReceita(l) ? v < 0 : v > 0;
}

/**
 * Valor a GRAVAR a partir do que a pessoa digitou. Na tela ela digita sempre
 * positivo: receita grava positivo, despesa grava negativo, e `estorno`
 * inverte os dois.
 */
export function valorParaGravar(digitado: number, tipo: TipoLancamento, estorno: boolean): number {
  const modulo = Math.abs(Number(digitado) || 0);
  const base = tipo === 'receita' ? modulo : -modulo;
  const v = estorno ? -base : base;
  return v === 0 ? 0 : v; // evita -0, que confunde na comparação e no JSON
}

/**
 * Dinheiro para a tela, com o sinal à mostra. O valor cru da despesa é
 * negativo por convenção e SEMPRE foi exibido sem o menos (é uma despesa, a
 * tela já diz isso) — então o menos só aparece quando o lançamento é estorno,
 * que é justamente quando ele significa alguma coisa.
 */
export function dinheiroComSinal(l: LancamentoSinal): string {
  const bruto = 'R$ ' + Math.abs(l.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
  return ehEstorno(l) ? '− ' + bruto : bruto;
}
